(() => {
  const CONFIG_ELEMENT_ID = 'CampaignGiftConfig';
  const UI_SELECTOR = '[data-campaign-cart-progress]';
  const MESSAGE_SELECTOR = '[data-campaign-cart-progress-message]';
  const BAR_SELECTOR = '[data-campaign-cart-progress-bar]';

  const GIFT_ITEM_ATTR = 'data-campaign-gift-item';

  const state = {
    syncing: false,
    lastSyncAt: 0,
    lastCartToken: null
  };

  const collectionCache = {};
  const giftStockState = {
    ok: null, // null = unknown, true/false = last known
    lastCheckedAt: 0,
    lastVariantId: 0
  };

  const STOCK_CHECK_TTL_MS = 15000;
  const STOCK_POLL_INTERVAL_MS = 20000;
  // Cart-based blocker:
  // - Vimla: block when any cart line item has a properties key matching
  //   /vimla[\s\-_]?abonnemang/i with a non-empty value
  // - Verification code: block when a line item has a properties key matching
  //   /verification_code/i and its value contains /VIMLA/i
  const VIMLA_PROPERTY_KEY_REGEX = /vimla[\s\-_]?abonnemang/i;
  const VERIFICATION_CODE_KEY_REGEX = /verification_code/i;
  const VERIFICATION_CODE_VALUE_REGEX = /vimla/i;

  function readConfig() {
    const el = document.getElementById(CONFIG_ELEMENT_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return null;
    }
  }

  function isEligibleChannel(config) {
    // Domain logic is deprecated; eligibility is now based on document.referrer + checkbox settings.
    return true;
  }

  function getReferrerSource() {
    const ref = String(document.referrer || '').toLowerCase();
    if (ref.includes('vimla')) return 'vimla';
    if (ref.includes('telenor')) return 'telenor';
    if (ref.includes('renewed')) return 'renewed';
    return 'renewed';
  }

  function isEligibleReferrerPage(config) {
    const source = getReferrerSource();
    const pages = config?.page_eligibility || {};
    const anyChannelConfigured = Boolean(pages.renewed || pages.vimla || pages.telenor);

    // Backward-safe fallback: if no channel is explicitly enabled, do not block campaign.
    if (!anyChannelConfigured) return true;

    if (source === 'vimla') return Boolean(pages.vimla);
    if (source === 'telenor') return Boolean(pages.telenor);
    return Boolean(pages.renewed);
  }

  function parseYyyyMmDd(dateStr) {
    const s = String(dateStr || '').trim();
    if (!s) return null;
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const year = Number(m[1]);
    const monthIndex = Number(m[2]) - 1;
    const day = Number(m[3]);
    // Interpret dates in store time-ish (client local). We only need coarse gating.
    return new Date(year, monthIndex, day, 0, 0, 0, 0);
  }

  function isWithinDateWindow(config) {
    const now = new Date();
    const start = parseYyyyMmDd(config?.campaign_start_date);
    const end = parseYyyyMmDd(config?.campaign_end_date);

    if (start && now < start) return false;
    if (end) {
      // Deactivate when current date > end date (end date inclusive).
      const endInclusive = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);
      if (now > endInclusive) return false;
    }
    return true;
  }

  function isCampaignEnabled(config) {
    return Boolean(config?.enable_campaign) && isWithinDateWindow(config) && isEligibleReferrerPage(config);
  }

  async function cartHasEligibleCollectionItem(cart, config) {
    const scope = config?.product_scope || 'all_products';
    if (scope !== 'specific_collection') return true;

    const handle = config?.eligible_collection?.handle;
    if (!handle) return false;

    const coll = await fetchCollectionProducts(handle);
    if (!coll || !coll.ids || !coll.ids.size) return false;

    if (!cart?.items?.length) return false;
    for (const item of cart.items) {
      if (coll.ids.has(Number(item.product_id))) {
        return true;
      }
    }
    return false;
  }

  function cartHasEligibleProductItem(cart, config) {
    const scope = config?.product_scope || 'all_products';
    if (scope !== 'specific_product') return true;

    const eligibleId = Number(config?.eligible_product?.id || 0);
    if (!eligibleId) return false;

    if (!cart?.items?.length) return false;
    return cart.items.some((item) => Number(item.product_id) === eligibleId);
  }

  async function cartSatisfiesProductScope(cart, config) {
    const scope = config?.product_scope || 'all_products';
    if (scope === 'specific_collection') {
      return await cartHasEligibleCollectionItem(cart, config);
    }
    if (scope === 'specific_product') {
      return cartHasEligibleProductItem(cart, config);
    }
    return true;
  }

  function cartContainsBlockedText(cart) {
    if (!cart?.items?.length) return false;
    return cart.items.some((item) => {
      const props = item?.properties;
      if (!props || typeof props !== 'object') return false;

      const keys = Object.keys(props);

      // Vimla abonnemang property
      var hasVimla = keys.some(function (key) {
        var keyStr = String(key || '').trim();
        if (!VIMLA_PROPERTY_KEY_REGEX.test(keyStr)) return false;
        var rawValue = props[key];
        var value = String(rawValue == null ? '' : rawValue).trim();
        return value.length > 0;
      });
      if (hasVimla) return true;

      // Verification code property
      var hasVerification = keys.some(function (key) {
        var keyStr = String(key || '').trim();
        if (!VERIFICATION_CODE_KEY_REGEX.test(keyStr)) return false;
        var rawValue = props[key];
        var value = String(rawValue == null ? '' : rawValue).trim();
        if (!value) return false;
        return VERIFICATION_CODE_VALUE_REGEX.test(value);
      });
      return hasVerification;
    });
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return await res.json();
  }

  async function fetchGiftProductJson(handle) {
    const safeHandle = String(handle || '').trim();
    if (!safeHandle) return null;
    const res = await fetch(`/products/${encodeURIComponent(safeHandle)}.js`, { credentials: 'same-origin' });
    if (!res.ok) return null;
    return await res.json();
  }

  async function isGiftVariantInStock(config, { force = false } = {}) {
    const gift = config?.gift_product;
    const handle = gift?.handle;
    const desiredVariantId = Number(gift?.variant_id || 0);
    if (!handle || !desiredVariantId) return false;

    const now = Date.now();
    const sameVariant = giftStockState.lastVariantId === desiredVariantId;
    if (!force && sameVariant && giftStockState.ok !== null && (now - giftStockState.lastCheckedAt) < STOCK_CHECK_TTL_MS) {
      return giftStockState.ok;
    }

    giftStockState.lastVariantId = desiredVariantId;
    giftStockState.lastCheckedAt = now;

    const productJson = await fetchGiftProductJson(handle);
    if (!productJson?.variants?.length) {
      giftStockState.ok = false;
      return false;
    }

    const variant = productJson.variants.find(v => Number(v.id) === desiredVariantId);
    if (!variant) {
      giftStockState.ok = false;
      return false;
    }

    // Selected variant only; align with Shopify purchasable state.
    // variant.available already reflects inventory policy + stock rules.
    if (!variant.available) {
      giftStockState.ok = false;
      return false;
    }

    giftStockState.ok = true;
    return true;
  }

  async function fetchCollectionProducts(handle) {
    const safeHandle = String(handle || '').trim();
    if (!safeHandle) return null;
    if (collectionCache[safeHandle]) return collectionCache[safeHandle];

    const res = await fetch(`/collections/${encodeURIComponent(safeHandle)}/products.json?limit=250`, {
      credentials: 'same-origin'
    });
    if (!res.ok) {
      collectionCache[safeHandle] = null;
      return null;
    }
    const data = await res.json();
    const ids = new Set();
    (data.products || []).forEach((p) => {
      if (p && typeof p.id !== 'undefined') {
        ids.add(Number(p.id));
      }
    });
    const entry = { ids };
    collectionCache[safeHandle] = entry;
    return entry;
  }

  function getGiftLineItem(cart, config) {
    const keyName = config?.gift_property_key || '_campaign_gift';
    const keyValue = config?.gift_property_value || config?.campaign_id;
    if (!cart?.items?.length) return null;

    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      const props = item?.properties || {};
      if (props && typeof props === 'object' && props[keyName] === keyValue) {
        return { item, line: i + 1 };
      }
    }
    return null;
  }

  function getAllGiftLineItems(cart, config) {
    const keyName = config?.gift_property_key || '_campaign_gift';
    const keyValue = config?.gift_property_value || config?.campaign_id;
    const out = [];
    if (!cart?.items?.length) return out;
    cart.items.forEach((item, idx) => {
      const props = item?.properties || {};
      if (props && typeof props === 'object' && props[keyName] === keyValue) {
        out.push({ item, line: idx + 1 });
      }
    });
    return out;
  }

  function formatMoney(cents) {
    // Leverage theme money formats if available (Prestige exposes these).
    const value = Number(cents || 0);
    const format = (window.theme && (window.theme.moneyWithCurrencyFormat || window.theme.moneyFormat)) || '{{amount}}';
    // Basic formatter for common Shopify patterns: {{amount}}, {{amount_no_decimals}}, etc.
    const amount = (value / 100).toFixed(2);
    const amountNoDecimals = (value / 100).toFixed(0);
    const amountWithComma = amount.replace('.', ',');
    const amountNoDecimalsWithComma = amountNoDecimals.replace('.', ',');

    if (format.includes('{{amount_no_decimals}}')) return format.replace('{{amount_no_decimals}}', amountNoDecimalsWithComma);
    if (format.includes('{{amount}}')) return format.replace('{{amount}}', amountWithComma);
    return amountWithComma;
  }

  async function renderProgressUI(cart, config) {
    const uiEls = document.querySelectorAll(UI_SELECTOR);
    if (!uiEls.length) return;

    const enabled = isCampaignEnabled(config);
    const threshold = Number(config?.cart_threshold || 0);
    const subtotal = Number(cart?.items_subtotal_price ?? cart?.total_price ?? 0);
    const productScopeEligible = await cartSatisfiesProductScope(cart, config);
    const hasBlockedCartItem = cartContainsBlockedText(cart);
    const giftInStock = await isGiftVariantInStock(config);

    uiEls.forEach((ui) => {
      if (!enabled || !threshold || threshold <= 0 || !productScopeEligible || hasBlockedCartItem || !giftInStock) {
        ui.hidden = true;
        return;
      }

      ui.hidden = false;

      const messageEl = ui.querySelector(MESSAGE_SELECTOR);
      const barFill = ui.querySelector(BAR_SELECTOR);

      const progressRatio = Math.max(0, Math.min(1, subtotal / threshold));
      const progressPct = Math.round(progressRatio * 100);

      ui.querySelector('.CampaignCartProgress__Bar')?.setAttribute('aria-valuenow', String(progressPct));
      if (barFill) barFill.style.width = `${progressPct}%`;

      const remaining = Math.max(0, threshold - subtotal);
      const reached = subtotal >= threshold;

      if (messageEl) {
        if (reached) {
          messageEl.textContent = String(config?.campaign_message_success || 'You unlocked a free gift!');
        } else {
          const template = String(config?.campaign_message_progress || 'You are {{amount}} away from receiving a free gift');
          // Use innerHTML so theme money formats that include markup (eg. <span class="money">) render correctly.
          messageEl.innerHTML = template
            .replace('{{amount}}', formatMoney(remaining))
            .replace('{{threshold}}', formatMoney(threshold));
        }
      }
    });
  }

  async function cartChangeLine(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ line, quantity })
    });
    if (!res.ok) throw new Error('Failed cart change');
    return await res.json();
  }

  async function cartAdd(variantId, properties) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: variantId, quantity: 1, properties: properties || {} })
    });
    if (!res.ok) throw new Error('Failed cart add');
    return await res.json();
  }

  async function ensureGiftStock(config) {
    const gift = config?.gift_product;
    if (!gift?.handle) return false;
    const productJson = await fetchGiftProductJson(gift.handle);
    if (!productJson?.variants?.length) return false;

    const desiredId = Number(gift.variant_id || 0);
    const variant = productJson.variants.find(v => Number(v.id) === desiredId);
    if (!variant) return false;

    // Selected variant only. Use Shopify availability (already policy-aware).
    if (!variant.available) return false;
    return { variantId: Number(variant.id) };
  }

  function decorateGiftLineUI(config) {
    const root = document.getElementById('sidebar-cart') || document;
    const keyName = config?.gift_property_key || '_campaign_gift';
    const keyValue = config?.gift_property_value || config?.campaign_id;

    root.querySelectorAll(`[data-line-item-properties]`).forEach((node) => {
      try {
        const props = JSON.parse(node.getAttribute('data-line-item-properties') || '{}');
        if (props && props[keyName] === keyValue) {
          const wrapper = node.closest('.CartItemWrapper') || node.closest('.CartItem') || node;
          wrapper.setAttribute(GIFT_ITEM_ATTR, 'true');
        }
      } catch (e) {}
    });
  }

  function bindGuards() {
    const root = document.getElementById('sidebar-cart') || document;
    if (root.__campaignGiftBound) return;
    root.__campaignGiftBound = true;

    root.addEventListener('click', async (e) => {
      const config = readConfig();
      if (!config) return;

      const removeLink = e.target.closest('[data-action="remove-item"]');
      const qtyLink = e.target.closest('[data-action="update-item-quantity"]');
      if (!removeLink && !qtyLink) return;

      const wrapper = e.target.closest(`[${GIFT_ITEM_ATTR}="true"]`);
      if (!wrapper) return;

      // If threshold is still met and campaign eligible, block removal/qty changes.
      try {
        const cart = await fetchCart();
        const enabled = isCampaignEnabled(config);
        const threshold = Number(config?.cart_threshold || 0);
        const subtotal = Number(cart?.items_subtotal_price ?? cart?.total_price ?? 0);
        const giftInStock = await isGiftVariantInStock(config);
        const productScopeEligible = await cartSatisfiesProductScope(cart, config);
        const hasBlockedCartItem = cartContainsBlockedText(cart);
        const shouldHaveGift = enabled && giftInStock && threshold > 0 && subtotal >= threshold && productScopeEligible && !hasBlockedCartItem;
        if (shouldHaveGift) {
          e.preventDefault();
          e.stopPropagation();
        }
      } catch (err) {
        // If we can't validate, allow default behavior.
      }
    }, true);
  }

  async function syncCampaign(trigger) {
    const config = readConfig();
    if (!config) return;

    // Always bind guards (safe idempotent).
    bindGuards();

    const now = Date.now();
    if (state.syncing) return;
    if (now - state.lastSyncAt < 250 && trigger !== 'force') return;

    state.syncing = true;
    state.lastSyncAt = now;

    try {
      const cart = await fetchCart();
      const giftInStock = await isGiftVariantInStock(config, { force: trigger === 'stock-poll' });

      // If gift is out of stock: completely disable campaign behavior.
      if (!giftInStock) {
        // Hide UI immediately
        document.querySelectorAll(UI_SELECTOR).forEach((ui) => { ui.hidden = true; });

        // Remove gift if it exists
        const giftLines = getAllGiftLineItems(cart, config);
        for (const gl of giftLines) {
          await cartChangeLine(gl.line, 0);
        }
        if (giftLines.length) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
        return;
      }

      await renderProgressUI(cart, config);

      const enabled = isCampaignEnabled(config);
      const threshold = Number(config?.cart_threshold || 0);
      const subtotal = Number(cart?.items_subtotal_price ?? cart?.total_price ?? 0);
      const productScopeEligible = await cartSatisfiesProductScope(cart, config); 
      const hasBlockedCartItem = cartContainsBlockedText(cart);

      const shouldHaveGift = enabled && threshold > 0 && subtotal >= threshold && productScopeEligible && !hasBlockedCartItem;

      const giftLines = getAllGiftLineItems(cart, config);
      if (!shouldHaveGift) {
        // Remove all campaign gifts if present.
        for (const gl of giftLines) {
          await cartChangeLine(gl.line, 0);
        }
        if (giftLines.length) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
        return;
      }

      // Campaign active: enforce single gift, qty=1, and add if missing.
      if (giftLines.length > 1) {
        // Keep first, remove the rest.
        for (let i = 1; i < giftLines.length; i++) {
          await cartChangeLine(giftLines[i].line, 0);
        }
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      const giftLine = giftLines[0] || null;
      if (giftLine) {
        if (Number(giftLine.item.quantity) !== 1) {
          await cartChangeLine(giftLine.line, 1);
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
        return;
      }

      // Missing gift: verify stock and add.
      const stock = await ensureGiftStock(config);
      if (!stock || !stock.variantId) return;

      const props = {};
      props[config.gift_property_key || '_campaign_gift'] = config.gift_property_value || config.campaign_id;
      await cartAdd(stock.variantId, props);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (err) {
      // Soft-fail: do nothing.
    } finally {
      state.syncing = false;
      // After any rerender, try to re-decorate gift line wrappers.
      const cfg = readConfig();
      if (cfg) decorateGiftLineUI(cfg);
    }
  }

  function setupObservers() {
    const drawer = document.getElementById('sidebar-cart');
    if (!drawer) return;

    const mo = new MutationObserver(() => {
      const config = readConfig();
      if (config) decorateGiftLineUI(config);
      syncCampaign('mutation');
    });

    mo.observe(drawer, { childList: true, subtree: true });
  }

  function init() {
    const config = readConfig();
    if (!config) return;

    decorateGiftLineUI(config);
    bindGuards();
    setupObservers();

    document.addEventListener('DOMContentLoaded', () => syncCampaign('force'));
    document.addEventListener('product:added', () => syncCampaign('product:added'));
    document.addEventListener('cart:refresh', () => {
      // Allow theme to rerender then sync again.
      setTimeout(() => syncCampaign('cart:refresh'), 50);
    });

    // Dynamic stock re-check (no page reload needed).
    setInterval(async () => {
      const cfg = readConfig();
      if (!cfg) return;
      try {
        const before = giftStockState.ok;
        const nowOk = await isGiftVariantInStock(cfg, { force: true });
        if (before !== null && before !== nowOk) {
          syncCampaign('stock-poll');
        }
      } catch (e) {}
    }, STOCK_POLL_INTERVAL_MS);
  }

  init();
})();

